import { compileSchema, type SchemaNode, type JsonError, isJsonError } from "json-schema-library";
import { type JsonSchemaProperty } from "../VdmMapperData";

const ROOT_ARRAY_LABEL = "Array";

/**
 * Parses a raw JSON Schema string into the `JsonSchemaProperty[]` tree
 * used by the VDM panel for rendering.
 */
export function parseJsonSchemaToProperties(schemaString: string): JsonSchemaProperty[] {
    if (!schemaString || schemaString.trim().length === 0) {
        return [];
    }

    let node: SchemaNode;
    try {
        node = compileSchema(JSON.parse(schemaString));
    } catch {
        return [];
    }

    return buildRootProperties(node);
}

export function validateAndParseSchema(schemaString: string): { errors: string[]; properties: JsonSchemaProperty[] } {
    let node: ReturnType<typeof compileSchema>;
    try {
        node = compileSchema(JSON.parse(schemaString));
    } catch (e) {
        return { errors: [e instanceof Error ? e.message : "Invalid JSON Schema"], properties: [] };
    }

    const errors = (node.schemaErrors ?? []).map((e: JsonError) => e.message);
    const properties = errors.length === 0 ? buildRootProperties(node) : [];
    return { errors, properties };
}

function buildRootProperties(node: SchemaNode): JsonSchemaProperty[] {
    const resolved = resolve(node);
    if (getType(resolved) !== "array") {
        return buildPropertiesFromNode(resolved);
    }

    return [{ ...buildProperty(ROOT_ARRAY_LABEL, resolved), pathSegment: "" }];
}

function resolve(node: SchemaNode): SchemaNode {
    if (!node.$ref) {
        return node;
    }

    const resolved = node.resolveRef();
    return isJsonError(resolved) ? node : resolved;
}

function getType(node: SchemaNode): string {
    if (typeof node.type === "string") {
        return node.type;
    }

    if (Array.isArray(node.type)) {
        const nonNull = node.type.filter((t) => t !== "null");
        return nonNull.length > 0 ? nonNull[0] : "null";
    }

    if (node.allOf) {
        for (const sub of node.allOf) {
            const t = getType(resolve(sub));
            if (t) {
                return t;
            }
        }
    }

    const branches = node.anyOf ?? node.oneOf;
    if (branches) {
        for (const sub of branches) {
            const t = getType(resolve(sub));
            if (t && t !== "null") {
                return t;
            }
        }
    }

    if (node.properties) {
        return "object";
    }

    if (node.items) {
        return "array";
    }

    return "";
}

/** Collect all properties from a node, including those spread across allOf branches. */
function collectProperties(node: SchemaNode): Map<string, SchemaNode> {
    const props = new Map<string, SchemaNode>();

    if (node.properties) {
        for (const [key, propNode] of Object.entries(node.properties)) {
            props.set(key, propNode);
        }
    }

    if (node.allOf) {
        for (const sub of node.allOf) {
            for (const [key, propNode] of collectProperties(resolve(sub))) {
                if (!props.has(key)) {
                    props.set(key, propNode);
                }
            }
        }
    }

    return props;
}

function buildPropertiesFromNode(node: SchemaNode): JsonSchemaProperty[] {
    const resolved = resolve(node);
    const type = getType(resolved);
    const allProperties = collectProperties(resolved);

    if (type === "object" || allProperties.size > 0) {
        return Array.from(allProperties.entries()).map(([key, propNode]) =>
            buildProperty(key, propNode),
        );
    }

    // anyOf / oneOf — pick first branch that has structure
    const branches = resolved.anyOf ?? resolved.oneOf;
    if (branches) {
        for (const branch of branches) {
            const result = buildPropertiesFromNode(branch);
            if (result.length > 0) {
                return result;
            }
        }
    }

    return [];
}

function buildProperty(key: string, node: SchemaNode): JsonSchemaProperty {
    const resolved = resolve(node);
    const type = getType(resolved);

    if (type === "array") {
        return buildArrayProperty(key, resolved);
    }

    const children = buildChildProperties(resolved, type);

    return {
        key,
        type,
        ...(children.length > 0 ? { children } : {}),
    };
}

function buildArrayProperty(key: string, node: SchemaNode): JsonSchemaProperty {
    const { depth, itemNode, itemType } = unwrapArrayItems(node);
    const children = itemType === "object"
        ? buildChildProperties(itemNode, itemType)
        : [];

    if (children.length > 0) {
        const property: JsonSchemaProperty = {
            key,
            type: "array",
            children,
        };

        if (depth > 1) {
            property.displayType = getArrayDisplayType(depth - 1);
            property.collapsedDisplayType = getArrayDisplayType(depth);
        }

        return property;
    }

    return {
        key,
        type: "array",
        displayType: itemType && itemType !== "array"
            ? `${itemType}${"[]".repeat(depth)}`
            : getArrayDisplayType(depth),
    };
}

function buildChildProperties(node: SchemaNode, type: string): JsonSchemaProperty[] {
    if (type === "object") {
        const props = collectProperties(node);
        return Array.from(props.entries()).map(([key, propNode]) =>
            buildProperty(key, propNode),
        );
    }

    return [];
}

function unwrapArrayItems(node: SchemaNode): { depth: number; itemNode: SchemaNode; itemType: string } {
    let depth = 0;
    let current = node;

    while (getType(current) === "array" && current.items) {
        depth++;
        current = resolve(current.items);
    }

    return {
        depth,
        itemNode: current,
        itemType: getType(current),
    };
}

function getArrayDisplayType(depth: number): string {
    return `array${"[]".repeat(Math.max(depth, 1))}`;
}
